import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Actions from '../Actions';
import Search from '../Search';
import Button from '../Button';
import FontAwesomeIcon from '../Icon';
import Table from '../Table';
import Empty from '../Empty';
import Overlay from '../Overlay';
import Loader from '../Loader';
import { PATHS, ICONS } from 'src/common/constants';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { selectSearch } from 'src/store/selectors/search';
import { selectLoader } from 'src/store/selectors/loader';
import { variables } from 'src/Tenants';
import { checkSparkDataAvailable } from 'src/helpers/validationHelper';
import { onRequestPhoneCall } from 'src/helpers/validationHelper';
import { createPropertyDevelopersTableColumns } from 'src/components/PropertyDevelopersTable/helpers';
import {
  ContactFormActionsContext,
  useContactFormActionsFromContext,
} from 'src/SharedCommonContext/ContactFormContext';
import { ITableColumn } from 'src/components/Table/types';
import ContactForm from 'src/components/ContactForm/ContactForm';
import { fetchPropertyCounts } from 'src/store/actions/properties';
import {
  fetchContactForSuiteByIdFromAPI, sendActivationLink, sendResetPasswordLink,
} from 'src/utils/CommonHelpers/ContactAPIOperations/commonContactDataHelpers';
import {
  useContactFormSubmissionProperties
} from 'src/pages/SharedPageCustomHooks/UseContactsSavePropertiesPage/useContactFormSubmissionProperty';

const PropertyDevelopersTable = ({
  propertyDevelopers,
  onSearch,
  onDeleteContact,
  propertyData,
  onRefreshDevelopers
}: any) => {
  const user = useSelector(selectAuthenticatedUser);
  const search = useSelector(selectSearch);
  const loader = useSelector(selectLoader);

  const dispatch = useDispatch();

  const onFetchPropertyCounts = (propertyId: any, userId: any, roleName: any) =>
    dispatch(fetchPropertyCounts(propertyId, userId, roleName));

  const [showOverlay, setShowOverlay] = useState(false);
  const [editingExistingContactInTable, setEditingExistingContactInTable] =
    useState<boolean>(false);
  const [editingContactRole, setEditingContactRole] = useState<string | null>(
    null
  );

  const confirmEditingContact =
    editingExistingContactInTable && editingContactRole
      ? [editingContactRole] // Only show current role when editing.
      : ['Property Developer', 'Service Manager']; // Default roles when creating.

  const {
    clearContactFormData,
    setContactFormData,
    initializeContactFormDataFromEdit,
  }: ContactFormActionsContext = useContactFormActionsFromContext();

  useEffect(() => {
    setShowOverlay(false);
  }, []);

  const handleSetContactFormData = async (contactId: string) => {
    try {
      setEditingExistingContactInTable(true);

      const contactData: any = await fetchContactForSuiteByIdFromAPI(
        contactId,
        user.id
      );

      setEditingContactRole(contactData.roles?.[0]?.role?.name || null);

      initializeContactFormDataFromEdit({
        ...contactData,
        isPrimaryPropertyDeveloperContact: contactData.isPrimaryContact,
      });

      setShowOverlay(true);
    } catch (error) {
      console.error('Failed to fetch contact details:', error);
    }
  };

  const developerTableConfig: ITableColumn[] = useMemo(
    () =>
      createPropertyDevelopersTableColumns({
        propertyData,
        user,
        handleSetContactFormData,
        onDeleteContact,
        PATHS,
        ICONS,
        variables,
        onRequestPhoneCall,
        checkSparkDataAvailable,
      }),
    [propertyData, user, handleSetContactFormData, onDeleteContact]
  );

  const propertyManagerId = propertyData?.propertyManager?.id;

  const handleClosePropertyDeveloperForm = () => {
    setShowOverlay(false);
    clearContactFormData();
    setEditingExistingContactInTable(false);
    setEditingContactRole(null);
  };

  const handleOpenAddContactForm = () => {
    clearContactFormData();

    const initialProperties: any = currentProperty
      ? [
          {
            propertyId: currentProperty.propertyId || currentProperty.id,
            id: currentProperty.propertyId || currentProperty.id,
            name: currentProperty.name,
          },
        ]
      : [];

    setContactFormData({
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      type: 'default',
      userRole: 'Property Developer',
      isPrimaryContact: false,
      properties: initialProperties,
      propertyId: currentProperty.propertyId,
    });

    setShowOverlay(true);
  };
  
  const { handleSubmitContactForm } = useContactFormSubmissionProperties({
    propertyId: propertyData?.propertyId || propertyData?.id,
    adminContactId: propertyManagerId || user.id, // Required for adding existing users.
    editingExistingContactInTable: editingExistingContactInTable,
    onSuccess: () =>
      onFetchPropertyCounts(
        propertyData.propertyId,
        user.id,
        user.roles[0].role.name
      ),
    onClose: handleClosePropertyDeveloperForm,
    onRefreshDevelopers
  });

  const currentProperty: any = useMemo(() => {
    if (!propertyData) return null;

    return {
      propertyId: propertyData.id || propertyData.propertyId,
      id: propertyData.id || propertyData.propertyId,
      name:
        propertyData.name || propertyData.propertyName || 'Current Property',
    };
  }, [propertyData]);
  
  return (
    <>
      {loader ? <Loader center={true} /> : null}
      <Actions compact>
        <Search
          size="small"
          placeholder={'Search People...'}
          onChange={onSearch}
          theme={undefined}
        />
        {user.roles[0].role.name === 'Admin' ||
        user.roles[0].role.name === 'Super Admin' ||
        user.roles[0].role.name === 'Property Developer' ? (
          checkSparkDataAvailable(
            propertyData,
            user.roles[0].role.name
          ) ? null : (
            <Button
              color="teal"
              label={'Add Contact'}
              onClick={handleOpenAddContactForm}
              icon={
                <FontAwesomeIcon
                  glyph="user-plus"
                  weight="regular"
                  size="small"
                />
              }
              size="small"
            />
          )
        ) : null}
      </Actions>
      {propertyDevelopers && propertyDevelopers.length ? (
        <>
          <Table columns={developerTableConfig} data={propertyDevelopers} />
        </>
      ) : (
        <Empty
          direction="ttb"
          headline={'No people yet'}
          strapline={
            "Keep all your contacts here so it's easier for you to manage."
          }
          searchIsActive={search && search.contacts ? search.contacts : ''}
          cta={
            checkSparkDataAvailable(
              propertyData,
              user.roles[0].role.name
            ) ? null : (
              <>
                <Button
                  color="teal"
                  label={'Add Person'}
                  onClick={handleOpenAddContactForm}
                  icon={
                    <FontAwesomeIcon glyph="user" weight="light" size="small" />
                  }
                  size="small"
                />
              </>
            )
          }
          image={<img alt="No people yet" src="/assets/empty/contacts.svg" />}
        />
      )}
      <Overlay
        showOverlay={showOverlay}
        content={
          <ContactForm
            onClose={handleClosePropertyDeveloperForm}
            onSubmit={handleSubmitContactForm}
            sendResetPasswordLinkToUser={sendResetPasswordLink}
            sendActivationLinkToUser={sendActivationLink}
            allowedRoles={confirmEditingContact}
            pageUsedFrom="Properties"
            editingExistingContactInTable={editingExistingContactInTable}
          />
        }
      />
    </>
  );
};

export default PropertyDevelopersTable;
